import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { GraphDetails } from "@features/builder/types"
import { Autocomplete, TextField } from "@mui/material"
import { useGetScenarioSourceMnemonicsQuery } from "@store/services/graphsService"
import { getNormalisedGraphKey } from "@utils/graph"
import { ReactNode, useMemo } from "react"
import { Control, Controller, FieldValues, Path } from "react-hook-form"

const EMPTY_SCENARIO_MNEMONIC_OPTION =
  "No scenario data mnemonics found in the attached scenario file."

type ScenarioMnemonicFieldProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>
  name: Path<TFieldValues>
  label?: string
  children?: ReactNode
}

const ScenarioMnemonicField = <TFieldValues extends FieldValues>(
  props: ScenarioMnemonicFieldProps<TFieldValues>,
) => {
  const graphDocumentData = useGraphDocumentData()
  const graphData: GraphDetails | null = JSON.parse(graphDocumentData.data)
  const graphKey = graphData ? getNormalisedGraphKey(graphData) : ""

  const scenarioFilePath = graphData?.ScenarioSource
  const scenarioVersion = graphData?.ScenarioVersion

  const getMnemonicsQuery = useGetScenarioSourceMnemonicsQuery(
    {
      graphKey,
      type: "scenario",
      // Include scenarioVersion to ensure query refetches when scenario file changes
      scenarioVersion: scenarioVersion ?? "",
    },
    {
      skip: !scenarioFilePath,
    },
  )

  const mnemonics = useMemo(
    () => (scenarioFilePath ? getMnemonicsQuery.data ?? [] : []),
    [getMnemonicsQuery.data, scenarioFilePath],
  )

  const scenarioOptions = useMemo(
    () =>
      scenarioFilePath && mnemonics.length === 0
        ? [EMPTY_SCENARIO_MNEMONIC_OPTION]
        : mnemonics.map(({ mnem }) => mnem),
    [scenarioFilePath, mnemonics],
  )

  return (
    <Controller
      control={props.control}
      name={props.name}
      render={({ field, fieldState }) => (
        <Autocomplete
          freeSolo
          options={scenarioOptions}
          getOptionDisabled={(option) => option === EMPTY_SCENARIO_MNEMONIC_OPTION}
          size="small"
          fullWidth
          renderInput={(params) => (
            <>
              <TextField
                label={props.label}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                name={field.name}
                {...params}
                onChange={!scenarioFilePath ? (e) => field.onChange(e.target.value) : undefined}
                InputLabelProps={{ shrink: true, ...params.InputLabelProps }}
              />

              {props.children}
            </>
          )}
          componentsProps={{ popper: { style: { width: "fit-content" } } }}
          {...field}
          onChange={(_event, value) => field.onChange(value ?? "")}
          onInputChange={(_event, value, reason) => {
            if (reason === "input" || reason === "clear") {
              field.onChange(value)
            }
          }}
        />
      )}
    />
  )
}

export default ScenarioMnemonicField
